import React, { useCallback, useMemo } from 'react';
import { FileTextIcon, ExpandIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { openFilePreview } from './fileUtils';
import { usePdfThumbnail } from './usePdfThumbnail';

const sizeMap = {
  sm: 'size-8',
  md: 'size-14',
  lg: 'size-20',
  xl: 'h-40 w-full',
  full: 'size-full',
} as const;

const expandIconSize = {
  sm: 'size-3',
  md: 'size-4',
  lg: 'size-5',
  xl: 'size-6',
  full: 'size-5',
} as const;

const fallbackIconSize = {
  sm: 'size-3',
  md: 'size-5',
  lg: 'size-6',
  xl: 'size-8',
  full: 'size-6',
} as const;

export type FileThumbnailSize = keyof typeof sizeMap;
export type FileThumbnailType = 'image' | 'pdf' | 'unknown' | 'auto';

function detectFileType(src: string): 'image' | 'pdf' | 'unknown' {
  const lower = src.toLowerCase();
  if (lower.includes('.pdf') || lower.startsWith('data:application/pdf')) return 'pdf';
  if (
    lower.includes('.jpg') || lower.includes('.jpeg') ||
    lower.includes('.png') || lower.includes('.webp') ||
    lower.includes('.gif') || lower.includes('.svg') ||
    lower.startsWith('data:image/')
  ) return 'image';
  return 'image';
}

export type FileThumbnailProps = {
  /** Blob URL or remote URL for the file. */
  src: string;
  /** Content type. Use `'auto'` to detect from the URL. @default 'auto' */
  type?: FileThumbnailType;
  /** Thumbnail dimensions. @default 'md' */
  size?: FileThumbnailSize;
  /** Show an expand overlay on hover and open in new tab on click. @default true */
  expandable?: boolean;
  className?: string;
};

/**
 * Renders an image or PDF thumbnail with an optional expand-on-hover overlay.
 * Used internally by FileUploader and FileUploaderMulti.
 */
export const FileThumbnail: React.FC<FileThumbnailProps> = ({
  src,
  type = 'auto',
  size = 'md',
  expandable = true,
  className,
}) => {
  const resolvedType = useMemo(
    () => (type === 'auto' ? detectFileType(src) : type),
    [type, src],
  );

  // PDFs: render the first page once via pdf.js (off the main thread
  // in a worker) and cache the resulting PNG data URL by `src`. The
  // old approach used a live `<object data=…>` per thumbnail, which
  // boots the browser's full PDF engine on every mount — fine for a
  // single thumbnail, but two side-by-side (signed contract + signed
  // quotation) made the agreement edit modal feel laggy on open and
  // re-render the document every time the modal was reopened. With
  // the cached approach the second open is instant and there's no
  // main-thread render work past the first page render.
  const isPdf = resolvedType === 'pdf';
  const pdfThumb = usePdfThumbnail(isPdf ? src : null);
  const renderIconFallback =
    resolvedType === 'unknown' || (isPdf && !pdfThumb);

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (!expandable) return;
    e.stopPropagation();
    openFilePreview(src);
  }, [expandable, src]);

  return (
    <button
      type="button"
      className={cn(
        'group/thumb relative rounded-lg border border-gray-200 shrink-0 overflow-hidden bg-white',
        sizeMap[size],
        expandable ? 'cursor-pointer' : 'cursor-default',
        className,
      )}
      onClick={handleClick}
      tabIndex={expandable ? 0 : -1}
    >
      {resolvedType === 'image' && (
        <img src={src} alt="preview" className="size-full object-cover" />
      )}

      {isPdf && pdfThumb && (
        <img
          src={pdfThumb}
          alt="PDF preview"
          className="size-full object-cover object-top"
        />
      )}

      {renderIconFallback && (
        <div className="flex items-center justify-center size-full">
          <FileTextIcon className={cn(fallbackIconSize[size], 'text-gray-400')} />
        </div>
      )}

      {expandable && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover/thumb:bg-black/30 transition-colors rounded-lg">
          <ExpandIcon
            className={cn(
              'text-white opacity-0 group-hover/thumb:opacity-100 transition-opacity',
              expandIconSize[size],
            )}
          />
        </div>
      )}
    </button>
  );
};
